import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './Auth/AuthContext';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000'
  : 'https://orca-app-byzgz.ondigitalocean.app';

function StudyGuides() {
  const [guides, setGuides] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { userEmail } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const guidesResponse = await axios.get(`${API_BASE_URL}/api/study-guides`, {
          params: { userId: userEmail },
        });
        setGuides(guidesResponse.data);

        const subjectsResponse = await axios.get(`${API_BASE_URL}/api/study-guides/subjects`);
        setSubjects(subjectsResponse.data);

        setIsLoading(false);
      } catch (err) {
        console.error('Fetch error:', err);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userEmail]);

  const handleSubjectToggle = (subject) => {
    setSelectedSubjects(prev =>
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  };

  const filteredGuides = guides.filter(guide =>
    (selectedSubjects.length === 0 || selectedSubjects.includes(guide.subject)) &&
    guide.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openGuide = (guide) => {
    if (guide.pdfUrl) {
      setSelectedGuide(guide);
    } else {
      alert('Upgrade to Premium to unlock full study guides!');
    }
  };

  const closeGuide = () => {
    setSelectedGuide(null);
  };

  if (isLoading) return <p className="text-study-black text-center pt-10">Loading...</p>;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center font-quicksand pt-10 px-4">
      <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-study-teal to-study-pink mb-2">
        Study Guides
      </h1>
      <p className="text-study-black text-lg mb-8">Ace your exams with our expert guides!</p>

      <input
        type="text"
        placeholder="Search by title..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full max-w-md p-2 mb-4 border-2 border-study-teal rounded-lg focus:outline-none focus:border-study-pink animate-pulse"
      />

      <div className="flex flex-wrap gap-2 mb-6 max-w-md overflow-x-auto">
        {subjects.map(subject => (
          <button
            key={subject}
            onClick={() => handleSubjectToggle(subject)}
            className={`px-3 py-1 rounded-full text-sm transition-all ${
              selectedSubjects.includes(subject)
                ? 'bg-study-pink text-white'
                : 'bg-study-teal text-study-black hover:bg-study-yellow'
            }`}
          >
            {subject}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-6xl">
        {filteredGuides.map(guide => (
          <div
            key={guide.title}
            onClick={() => openGuide(guide)}
            className="bg-white rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all cursor-pointer animate-bounce-in"
            style={{ animationDelay: `${Math.random() * 0.3}s` }}
          >
            <div className="p-4">
              <span className="inline-block px-2 py-1 bg-study-teal text-white text-xs rounded-full mb-2">
                {guide.subject}
              </span>
              <img
                src={guide.thumbnailUrl}
                alt={`${guide.title} thumbnail`}
                className="w-full h-32 object-cover rounded-md mb-2"
              />
              <h3 className="text-study-black text-lg font-bold">{guide.title}</h3>
              <p className="text-study-black text-sm">{guide.teaser}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 animate-slide-up">
          <div className="relative w-full h-full max-w-4xl max-h-[90vh] bg-white rounded-lg p-4">
            <button
              onClick={closeGuide}
              className="absolute top-2 right-2 bg-study-pink text-study-black p-2 rounded-full hover:bg-study-yellow"
            >
              X
            </button>
            <embed
              src={selectedGuide.pdfUrl}
              type="application/pdf"
              width="100%"
              height="100%"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Bounce-in animation
const styles = `
  @keyframes bounce-in {
    0% { opacity: 0; transform: scale(0.9); }
    60% { opacity: 1; transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  .animate-bounce-in {
    animation: bounce-in 0.5s ease-out forwards;
  }
  @keyframes slide-up {
    0% { transform: translateY(100%); }
    100% { transform: translateY(0); }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

export default StudyGuides;